import * as React from 'react'
import { createContext, useContext, useEffect, useState, useCallback, useRef, PropsWithChildren, memo } from 'react'
import { PylonProvider as BasePylonProvider, usePylon as useBasePylon } from '@bolasim/react-use-pylon'
import { useUser } from '@renderer/hooks/useUser'
import { getUserFullName } from '@renderer/utils/user-name'
import { useAppVersion } from '@renderer/hooks/useAppVersion'
import { usePersonalization } from '@renderer/hooks/usePersonalization'
import { v1Client } from '@renderer/chat'
import { useAuth } from '@renderer/chat/providers'
import { useIsDark, Interactive } from '@highlight/ui'

const iframeHeadWatchers = new WeakMap<HTMLIFrameElement, MutationObserver>()

/** Pylon container selectors in our document (they wrap the iframes); we register interactive bounds for these (see PylonInteractiveRegions) */
const PYLON_INTERACTIVITY_SELECTORS = [
  '.PylonChat-chatWindowFrameContainer',
  '.PylonChat-bubbleFrameContainer',
] as const

/**
 * Injects styles into an iframe's document
 * Abstracted function that can be used for any iframe
 *
 * @returns boolean indicating whether injection succeeded
 */
function injectStylesIntoIframe(iframe: HTMLIFrameElement, styles: string): boolean {
  try {
    const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document
    if (!iframeDocument) {
      return false
    }

    // Check if styles already injected
    const existingStyle = iframeDocument.getElementById('pylon-custom-styles')
    if (existingStyle) {
      existingStyle.textContent = styles
      return true
    }

    // Create and inject style element
    const styleElement = iframeDocument.createElement('style')
    styleElement.id = 'pylon-custom-styles'
    styleElement.textContent = styles
    const head = iframeDocument.head || iframeDocument.getElementsByTagName('head')[0]

    // If head is not yet available, signal caller to retry later
    if (!head) {
      return false
    }

    head.appendChild(styleElement)
    return true
  } catch (error) {
    console.error('[Pylon] Failed to inject styles into iframe:', error)
    return false
  }
}

/**
 * Injects our bubble skin into the pylon-chat-bubble iframe once Pylon is loaded.
 *
 * Only the bubble is restyled: it is deliberately a 36px chrome-matching square (paired with the
 * sizing in styles/index.css) rather than Pylon's brand bubble. The chat window itself is themed by
 * Pylon — see the `setTheme` call in PylonInner.
 */
function PylonBubbleStyles() {
  const { isReady } = usePylon()
  const isDark = useIsDark()

  useEffect(() => {
    if (!isReady) return

    const cleanups: Array<() => void> = []

    /**
     * Attempts to inject styles into an iframe by ID.
     * Handles both immediate injection and waiting for iframe load.
     * Registers cleanups so theme changes re-run cleanly (no stale watchers/listeners).
     */
    const injectStylesForIframe = (iframeId: string, styles: string) => {
      const iframe = document.getElementById(iframeId) as HTMLIFrameElement
      if (!iframe) return

      const attemptInjection = () => {
        try {
          const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document
          if (iframeDocument) {
            const success = injectStylesIntoIframe(iframe, styles)

            if (!success) {
              // If head is not yet available, watch for it to appear
              if (!iframeHeadWatchers.has(iframe)) {
                const observer = new MutationObserver(() => {
                  try {
                    const innerDoc = iframe.contentDocument || iframe.contentWindow?.document
                    if (!innerDoc) return

                    const head = innerDoc.head || innerDoc.getElementsByTagName('head')[0]
                    if (head) {
                      const injected = injectStylesIntoIframe(iframe, styles)
                      if (injected) {
                        observer.disconnect()
                        iframeHeadWatchers.delete(iframe)
                      }
                    }
                  } catch (error) {
                    console.error(`[Pylon] Error while observing iframe ${iframeId} head:`, error)
                  }
                })

                iframeHeadWatchers.set(iframe, observer)
                observer.observe(iframeDocument.documentElement || iframeDocument, {
                  childList: true,
                  subtree: true,
                })
                cleanups.push(() => {
                  observer.disconnect()
                  iframeHeadWatchers.delete(iframe)
                })
              }
            }
          }
        } catch (error) {
          // Iframe may not be accessible yet (cross-origin or not loaded)
          console.error(`[Pylon] Could not access iframe ${iframeId}:`, error)
        }
      }

      // Try immediately if iframe is already loaded
      attemptInjection()

      // Also listen for load event in case iframe loads later
      iframe.addEventListener('load', attemptInjection)
      cleanups.push(() => iframe.removeEventListener('load', attemptInjection))
    }

    // Styles for chat bubble iframe (theme-dependent; re-injected when isDark changes)
    const bubbleStyles = `
      .PylonChat-bubbleContainer {
        width: 36px !important;
        height: 36px !important;
        background: ${isDark ? '#222' : '#f1f1f1'} !important;
        border-radius: 8px !important;
        border: 1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        overflow: hidden !important;
        scale: 1 !important;
      }

      .PylonChat-bubbleContainer:hover {
        border: 1px solid ${isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'} !important;
        scale: 1 !important;
      }

      .PylonChat-bubbleContainer svg path {
        stroke: ${isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)'} !important;
      }

      .PylonChat-bubbleContainer>[data-is-showing=false] {
        display: none !important;
      }

      .PylonChat-bubbleUnreadIssues {
        background: hsl(348 100% 61%) !important;
        color: #fff !important;
        border-radius: 4px !important;
        position: absolute !important;
        top: 0px !important;
        right: 0px !important;
        font-size: 10px !important;
        font-weight: bold !important;
        padding: 0px 3px !important;
      }
    `

    // Inject (or update) styles when iframes are found; re-injects on theme change
    const tryInject = () => {
      injectStylesForIframe('pylon-chat-bubble', bubbleStyles)
    }

    // Try immediately so theme changes apply right away
    tryInject()

    // Watch for iframes being added to DOM
    const observer = new MutationObserver(() => {
      tryInject()
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })
    cleanups.push(() => observer.disconnect())

    return () => {
      cleanups.forEach((fn) => fn())
    }
  }, [isReady, isDark])

  return null
}

declare global {
  interface Window {
    Pylon?: (action: string, ...args: Array<unknown>) => void
  }
}

interface PylonContextType {
  isOpen: boolean
  isReady: boolean
  /**
   * The widget script has loaded and replaced Pylon's queueing stub. `isReady` flips on the stub
   * itself; a show issued before this is undone when the widget boots into its closed state.
   */
  isWidgetLoaded: boolean
  unreadCount: number
  show: () => void
  hide: () => void
  toggle: () => void
  showNewMessage: (message?: string) => void
}

const PylonContext = createContext<PylonContextType>({
  isOpen: false,
  isReady: false,
  isWidgetLoaded: false,
  unreadCount: 0,
  show: () => {},
  hide: () => {},
  toggle: () => {},
  showNewMessage: () => {},
})

interface PylonProviderProps {
  children: React.ReactNode
}

type PylonRect = { id: string; left: number; top: number; width: number; height: number }

function pylonRectsEqual(a: Array<PylonRect>, b: Array<PylonRect>): boolean {
  if (a.length !== b.length) return false
  return a.every((r, i) => {
    const o = b[i]
    return o.id === r.id && o.left === r.left && o.top === r.top && o.width === r.width && o.height === r.height
  })
}

/**
 * Registers interactive bounds that track the live Pylon container elements (chat window panel
 * + bubble/controls) so the overlay only consumes clicks over the widget — not the whole screen.
 *
 * Pylon's own hover-listeners can't make the widget interactive: the cursor sits over the Pylon
 * iframe, and pointer events over an iframe never reach the wrapping container, so the overlay
 * stays click-through. <Interactive> works because its tracker polls the OS cursor against these
 * registered rects regardless of iframes. Renders nothing in non-overlay renderers.
 */
function PylonInteractiveRegions() {
  const [rects, setRects] = useState<Array<PylonRect>>([])

  useEffect(() => {
    const measure = () => {
      const next: Array<PylonRect> = []
      PYLON_INTERACTIVITY_SELECTORS.forEach((selector) => {
        document.querySelectorAll<HTMLElement>(selector).forEach((el, i) => {
          const r = el.getBoundingClientRect()
          if (r.width > 0 && r.height > 0) {
            next.push({ id: `${selector}-${i}`, left: r.left, top: r.top, width: r.width, height: r.height })
          }
        })
      })
      setRects((prev) => (pylonRectsEqual(prev, next) ? prev : next))
    }

    measure()
    const interval = setInterval(measure, 149)

    return () => clearInterval(interval)
  }, [])

  return (
    <>
      {rects.map(({ id, left, top, width, height }) => (
        <div
          key={id}
          aria-hidden
          className="fixed"
          style={{ left, top, width, height, pointerEvents: 'none', zIndex: 0 }}
        >
          <Interactive />
        </div>
      ))}
    </>
  )
}

/** Apply visibility and pointer-events to Pylon chat container so it doesn't intercept clicks when closed */
function setShowChat(show: boolean): void {
  const el = document.getElementById('pylon-chat')
  if (!el) {
    return
  }
  el.style.visibility = show ? 'visible' : 'hidden'
  el.style.pointerEvents = show ? 'auto' : 'none'
}

/**
 * Inner provider that uses the base Pylon hook
 */
function PylonInner({ children }: { children: React.ReactNode }) {
  const user = useUser()
  const { version } = useAppVersion()
  const { getAuthToken } = useAuth()
  const { personalizationData } = usePersonalization()
  const basePylon = useBasePylon()
  const isDark = useIsDark()
  const [isReady, setIsReady] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [isWidgetLoaded, setIsWidgetLoaded] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const isOpenRef = useRef(isOpen)
  const lastFetchedEmailRef = React.useRef<string | null>(null)
  isOpenRef.current = isOpen

  // Detect when Pylon is ready and set up listeners
  useEffect(() => {
    const checkReady = () => {
      if (typeof window.Pylon === 'function') {
        // Hide on load
        window.Pylon?.('hideChatBubble')

        // Track unread count but keep hidden
        window.Pylon?.('onChangeUnreadMessagesCount', (count: number) => {
          setUnreadCount(count)
          window.Pylon?.('hideChatBubble')
        })

        // When Pylon opens internally
        window.Pylon?.('onShow', () => {
          setIsOpen(true)
          window.Pylon?.('showChatBubble')
        })

        // When Pylon closes internally - hide widget
        window.Pylon?.('onHide', () => {
          setIsOpen(false)
          window.Pylon?.('hideChatBubble')
        })

        console.log('Pylon is ready')
        setIsReady(true)

        return true
      }
      return false
    }

    if (checkReady()) {
      return
    }

    const interval = setInterval(() => {
      if (checkReady()) {
        clearInterval(interval)
      }
    }, 100)

    return () => {
      clearInterval(interval)
    }
  }, [])

  // Pylon ships its own light/dark palettes - keep the widget in sync with the app theme
  useEffect(() => {
    if (!isReady) return

    window.Pylon?.('setTheme', isDark ? 'dark' : 'light')
  }, [isReady, isDark])

  // React to isOpen state changes - hide widget when closed
  useEffect(() => {
    if (isReady) {
      if (!isOpen) {
        window.Pylon?.('hideChatBubble')
      }
      setShowChat(isOpen)
    }
  }, [isOpen, isReady])

  // The snippet's stub queues calls on `.q`; the loaded widget replaces it with a function that has none.
  useEffect(() => {
    if (!isReady || isWidgetLoaded) return

    const interval = setInterval(() => {
      if (window.Pylon && !('q' in window.Pylon)) {
        setIsWidgetLoaded(true)
      }
    }, 100)

    return () => clearInterval(interval)
  }, [isReady, isWidgetLoaded])

  // When chat is closed, enforce visibility on #pylon-chat when it appears (race: Pylon may inject it after we set isReady)
  useEffect(() => {
    if (!isReady) return

    const applyVisibility = () => {
      const el = document.getElementById('pylon-chat')
      if (el) {
        setShowChat(isOpenRef.current)
      }
    }

    applyVisibility()

    const observer = new MutationObserver(() => {
      applyVisibility()
    })
    observer.observe(document.body, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [isReady])

  // Safety: when chat is closed, re-apply hidden for a short window so late-injected or recreated Pylon containers stay non-interactive
  useEffect(() => {
    if (!isReady || isOpen) return

    const durationMs = 20_000
    const intervalMs = 500
    const interval = setInterval(() => setShowChat(false), intervalMs)
    const stop = setTimeout(() => clearInterval(interval), durationMs)

    return () => {
      clearInterval(interval)
      clearTimeout(stop)
    }
  }, [isReady, isOpen])

  // Poll to detect when Pylon closes itself (backup for onHide callback)
  useEffect(() => {
    if (!isReady || !isOpen) return

    let wasVisible = true
    const checkIfClosed = setInterval(() => {
      const pylonIframe = document.querySelector('[id^="pylon"] iframe') as HTMLIFrameElement
      if (pylonIframe) {
        const rect = pylonIframe.getBoundingClientRect()
        const isVisible = rect.height > 100 && rect.width > 100

        if (wasVisible && !isVisible) {
          setIsOpen(false)
        }
        wasVisible = isVisible
      }
    }, 300)

    return () => clearInterval(checkIfClosed)
  }, [isReady, isOpen])

  // Update user info
  useEffect(() => {
    if (!isReady) return

    const updatePylonSettings = async () => {
      try {
        const name = getUserFullName(user)?.trim() || personalizationData?.nickname?.trim() || user?.email

        if (!name) {
          console.log('[Pylon] No name available')
          return
        }

        basePylon.update({
          email: user?.email,
          name,
        })

        const customFields: Record<string, string> = {}
        if (user?.id) customFields.user_id = user.id
        if (version) customFields.app_version = version
        customFields.os_platform = process.platform === 'win32' ? 'Windows' : 'Mac'
        if (Object.keys(customFields).length > 0) {
          window.Pylon?.('setNewIssueCustomFields', customFields)
        }

        // Only fetch identity hash if email changed
        if (user?.email && user.email !== lastFetchedEmailRef.current) {
          try {
            const authToken = await getAuthToken()

            // @ts-ignore
            const response = (await v1Client.GET('/pylon-identity-hash', {
              headers: { Authorization: `Bearer ${authToken}` },
            })) as {
              data?: { success: true; data: { emailHash: string } }
              error?: unknown
            }

            if (response.data?.success && response.data.data.emailHash) {
              lastFetchedEmailRef.current = user.email
              basePylon.update({
                emailHash: response.data.data.emailHash,
              })
            }
          } catch (error) {
            console.error('[Pylon] Failed to fetch identity hash:', error)
          }
        }
      } catch (error) {
        console.error('[Pylon] Error updating settings:', error)
      }
    }

    updatePylonSettings()
  }, [
    user?.id,
    user?.email,
    user?.firstName,
    user?.lastName,
    personalizationData?.nickname,
    version,
    isReady,
    getAuthToken,
    basePylon,
  ])

  const show = useCallback(() => {
    if (!isReady) {
      console.warn('Cannot show, Pylon is not ready')
      return
    }

    const customFields: Record<string, string> = {}
    if (user?.id) customFields.user_id = user.id
    if (version) customFields.app_version = version
    customFields.os_platform = process.platform === 'win32' ? 'Windows' : 'Mac'
    window.Pylon?.('setNewIssueCustomFields', customFields)

    // The chat window iframe is created lazily on first open; re-assert the theme so it never
    // renders with Pylon's default palette
    window.Pylon?.('setTheme', isDark ? 'dark' : 'light')

    basePylon.show()
    setIsOpen(true)
  }, [isReady, user?.id, version, basePylon, isDark])

  const hide = useCallback(() => {
    setIsOpen(false)
    basePylon.hide()
  }, [basePylon])

  const toggle = useCallback(() => {
    if (isOpen) {
      hide()
    } else {
      show()
    }
  }, [isOpen, show, hide, isReady])

  const showNewMessage = useCallback(
    (message?: string) => {
      if (!isReady) return

      // `showNewMessage` alone doesn't open the widget: the chat iframe is
      // built on first open, and in the overlay the container stays hidden
      // until `isOpen` flips. Go through `show` for both.
      show()
      if (message) {
        window.Pylon?.('showNewMessage', message)
      } else {
        window.Pylon?.('showNewMessage')
      }
    },
    [isReady, show],
  )

  return (
    <PylonContext.Provider value={{ isOpen, isReady, isWidgetLoaded, unreadCount, show, hide, toggle, showNewMessage }}>
      {/* Keep just the Pylon widget area interactive while open (not the whole screen). */}
      {isReady && isOpen && <PylonInteractiveRegions />}
      {children}
    </PylonContext.Provider>
  )
}

const settings = { appId: '81f4d593-0a96-460d-8f33-97cf6515a68a' }
let hasInitializedPylon = false

/**
 * Pylon chat widget provider
 */
export const PylonProvider = memo(({ children }: PropsWithChildren<PylonProviderProps>) => {
  const [shouldInitialize, setShouldInitialize] = useState(false)

  useEffect(() => {
    if (!hasInitializedPylon) {
      hasInitializedPylon = true
      setShouldInitialize(true)
    }
  }, [])

  return (
    <BasePylonProvider chatSettings={settings} autoBoot={true} shouldInitialize={shouldInitialize}>
      <PylonInner>
        <PylonBubbleStyles />
        {children}
      </PylonInner>
    </BasePylonProvider>
  )
})
PylonProvider.displayName = 'PylonProvider'

export const usePylon = () => useContext(PylonContext)
